import React, { useEffect, useRef, useState } from 'react'
import { Color as newColor, colorToHex, hexToColor } from '@/components/modules/CanvasEditor/color'
import Chrome from '@uiw/react-color-chrome'
import { hexToHsva, rgbaToHsva } from '@uiw/color-convert'
import { type Color } from '@backend/lib/db/typeRoots/color'

interface ColorPickerQuickmenu {
  value: Color
  onChange: (color: Color, isHoverSelection?: boolean) => void
  colorPalette: string[]
  // Hide the alpha (transparency) slider when the consumer doesn't support it
  // (e.g., the moodboard brand-color swatch is always opaque).
  showAlpha?: boolean
}

export function ColorPickerQuickmenu({
  value,
  colorPalette,
  onChange,
  showAlpha = true,
}: ColorPickerQuickmenu) {
  const [hexValueForInput, setHexValueForInput] = useState(colorToHex(value).slice(0, 7))
  const [hsva, setHsva] = useState(() => {
    return rgbaToHsva({ r: value.r, g: value.g, b: value.b, a: value.a / 255 })
  })
  const [isDragging, setIsDragging] = useState(false)
  const lastColorRef = useRef(value)

  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hexWithoutAlpha = e.target.value.slice(0, 7)
    if (/^#[0-9A-F]{6}$/i.test(hexWithoutAlpha)) {
      const color = hexToColor(hexWithoutAlpha)
      const hsvaColor = hexToHsva(hexWithoutAlpha)
      setHsva({ ...hsvaColor, a: value.a / 255 })
      onChange(newColor({ ...color, a: value.a }))
    }
    setHexValueForInput(hexWithoutAlpha)
  }

  const handleColorPaletteInput = (index: number) => {
    if (index === value.index) {
      onChange({ ...value, index })
    } else {
      const hexColor = colorPalette[index]
      if (hexColor) {
        const hsvaColor = hexToHsva(hexColor)
        const color = hexToColor(hexColor)
        setHsva({ ...hsvaColor, a: value.a / 255 })
        setHexValueForInput(hexColor)
        onChange({ ...color, a: value.a, index })
      }
    }
  }

  const handleChromeChange = (color: {
    hex: string
    hsva: { h: number; s: number; v: number; a: number }
  }) => {
    setHsva(color.hsva)
    const newColor = hexToColor(color.hex)
    const alpha = Math.round(color.hsva.a * 255)
    const updatedColor = { ...newColor, a: alpha }

    lastColorRef.current = updatedColor

    if (!isDragging) {
      setIsDragging(true)
    }

    onChange(updatedColor, true)
    setHexValueForInput(color.hex)
  }

  const [prevValue, setPrevValue] = useState(value)
  if (prevValue !== value && !isDragging) {
    setPrevValue(value)
    setHsva(rgbaToHsva({ r: value.r, g: value.g, b: value.b, a: value.a / 255 }))
    setHexValueForInput(colorToHex(value).slice(0, 7))
  }

  useEffect(() => {
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false)
        onChange(lastColorRef.current, false)
      }
    }

    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [isDragging, onChange])

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <Chrome
          color={hsva}
          onChange={handleChromeChange}
          showAlpha={showAlpha}
          onMouseDown={() => {
            setIsDragging(true)
          }}
        />
        <div className="flex items-center gap-2">
          <div
            className="border-border h-8 w-8 rounded border"
            style={{ backgroundColor: hexValueForInput }}
          />
          <input
            type="text"
            value={hexValueForInput}
            onChange={handleHexInput}
            placeholder="#000000"
            className="bg-card text-foreground border-border flex h-8 rounded border px-2 text-sm"
          />
        </div>
      </div>

      {colorPalette.length > 0 && (
        <div>
          <p className="mb-2 text-[13px]">Color palette</p>
          <div className="grid grid-cols-5 gap-1">
            {colorPalette.map((color, i) => (
              <button
                key={i + color}
                className={`flex ${
                  value.index === i ? 'outline-foreground outline-solid outline-2' : ''
                } border-border relative h-8 w-8 cursor-pointer rounded border transition-transform hover:scale-110`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorPaletteInput(i)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
